import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, formatEUR, formatDate } from "@/components/app/Shared";
import OnboardingWizard from "@/components/app/OnboardingWizard";
import InvoiceTile from "@/components/app/InvoiceTile";
import { QuickInvoiceFab } from "@/components/app/QuickInvoiceCapture";
import { TrendingUp, Package, Truck, Building2, ShoppingCart, ArrowUpRight, ArrowRight, Clock, CheckCircle2, AlertTriangle, CalendarRange, Sparkles, ScanText, Zap, ShieldCheck, Users, ClipboardList, Calendar, XCircle, Circle, Plus, Trash2, Pencil, Save, X, GraduationCap, Award, LayoutTemplate } from "lucide-react";

export default function Dashboard() {
  const { user, showPrices, showSuppliers } = useAuth();
  const [stats, setStats] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [modules, setModules] = useState({ orders: true, invoices: true, documents: true });

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/user/modules").then((r) => setModules(r.data?.modules || { orders: true, invoices: true, documents: true })).catch(() => {});
  }, []);

  // Auto-open onboarding for org-admin if not done and no restaurants exist yet
  useEffect(() => {
    if (!user || !stats) return;
    const isAdmin = ["admin", "organization_admin"].includes(user.role);
    if (!isAdmin) return;
    const dismissed = localStorage.getItem("stockly_onboarding_dismissed") === "1";
    const done = localStorage.getItem("stockly_onboarding_done") === "1";
    if (dismissed || done) return;
    if ((stats.restaurants_count ?? 0) === 0) {
      setShowOnboarding(true);
    }
  }, [user, stats]);

  if (!stats) return <div className="p-10 text-zinc-500" data-testid="loading">Laden…</div>;

  const isAdmin = ["admin", "organization_admin"].includes(user?.role);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto" data-testid="dashboard-classic-page">
      {showOnboarding && <OnboardingWizard onClose={() => setShowOnboarding(false)} />}
      <PageHeader
        overline={isAdmin ? "Overzicht — alle restaurants" : `Mijn restaurant`}
        title={isAdmin ? `Hallo ${user?.name?.split(" ")[0]}.` : `Welkom, Chef ${user?.name?.split(" ")[0]}.`}
        description={isAdmin ? "Realtime inzicht in bestellingen, voorraad en leveranciers." : "Plaats nieuwe bestellingen en houd je voorraad in beeld."}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Link to="/" className="inline-flex items-center gap-1.5 h-10 px-3 border border-[#1B2D5C]/30 bg-[#1B2D5C]/5 hover:bg-[#1B2D5C]/10 rounded-md text-sm font-semibold text-[#1B2D5C]" data-testid="dashboard-open-today">
              <LayoutTemplate size={14} /> Naar Vandaag
            </Link>
            {isAdmin && (
              <button
                onClick={() => { localStorage.removeItem("stockly_onboarding_dismissed"); localStorage.removeItem("stockly_onboarding_done"); setShowOnboarding(true); }}
                className="inline-flex items-center gap-1.5 h-10 px-3 border border-zinc-200 hover:border-[#5BC044] hover:text-[#5BC044] rounded-md text-sm font-semibold text-zinc-700"
                data-testid="dashboard-open-onboarding"
              >
                <Sparkles size={14} /> Setup-gids
              </button>
            )}
            {!isAdmin && modules.orders && (
              <Link to="/nieuwe-bestelling" className="inline-flex items-center gap-2 h-10 px-4 bg-[#5BC044] hover:bg-[#4FAA3A] text-white rounded-md text-sm font-semibold" data-testid="dashboard-new-order-cta">
                Nieuwe bestelling <ArrowRight size={15} />
              </Link>
            )}
          </div>
        }
      />

      {/* Vaste volgorde: Compliance → Facturen → Keuringskalender → overige tegels */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6" data-testid="compliance-widgets">
        {isAdmin && <ComplianceCard />}
        <InvoiceTile hidden={!modules.invoices} />
        <InspectionsCard isAdmin={isAdmin} />
        <HygieneTasksCard isAdmin={isAdmin} />
        {isAdmin && <OccupancyCard />}
        {isAdmin && <TrainingProgressCard />}
      </div>
      {modules.invoices && <QuickInvoiceFab />}

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="kpi-grid">
        {modules.orders && <KpiCard icon={ShoppingCart} label="Totaal bestellingen" value={stats.total_orders} accent />}
        {isAdmin && <KpiCard icon={Building2} label="Restaurants" value={stats.restaurants_count} />}
        {modules.orders && <KpiCard icon={Package} label="Producten" value={stats.products_count} />}
        {showPrices && modules.orders && (
          <KpiCard
            icon={CalendarRange}
            label={`Uitgave deze maand (${new Date().toLocaleDateString("nl-NL", { month: "long" })})`}
            value={formatEUR(stats.monthly_spending ?? 0)}
          />
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {showSuppliers && <MetaCard icon={Truck} label="Leveranciers" value={stats.suppliers_count} />}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Recent orders */}
        {modules.orders && (
        <div className={`${isAdmin ? "lg:col-span-3" : "lg:col-span-5"} card-flat p-6`} data-testid="recent-orders-card">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="overline mb-1">Recente bestellingen</div>
              <h3 className="text-xl font-bold tracking-tight">Laatste activiteit</h3>
            </div>
            <Link to="/bestellingen" className="text-sm text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1">
              Alles tonen <ArrowUpRight size={14} />
            </Link>
          </div>
          {stats.recent_orders.length === 0 ? (
            <div className="text-sm text-zinc-500 py-8 text-center border border-dashed border-zinc-200 rounded-md">
              Nog geen bestellingen.
            </div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {stats.recent_orders.map((o) => (
                <Link to={`/bestellingen/${o.id}`} key={o.id} className="flex items-center justify-between py-3 hover:bg-zinc-50 -mx-2 px-2 rounded transition-colors" data-testid={`recent-order-${o.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{o.restaurant_name}</div>
                    <div className="text-xs text-zinc-500 font-mono mt-0.5">
                      {formatDate(o.created_at)} • {o.items.length} item{o.items.length === 1 ? "" : "s"}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    {showPrices && <div className="font-mono font-semibold text-sm">{formatEUR(o.total)}</div>}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Per-restaurant breakdown (admin only) */}
        {isAdmin && modules.orders && (
          <div className="lg:col-span-2 card-flat p-6" data-testid="restaurants-breakdown-card">
            <div className="overline mb-1">Per restaurant</div>
            <h3 className="text-xl font-bold tracking-tight mb-5 text-[#1B2D5C]">Bestellingen per locatie</h3>

            {stats.per_restaurant.length === 0 ? (
              <div className="text-sm text-zinc-500 py-6 text-center">Nog geen data.</div>
            ) : (
              <div className="space-y-3">
                {stats.per_restaurant.map((r) => {
                  const max = Math.max(...stats.per_restaurant.map((x) => x.count));
                  const pct = max ? (r.count / max) * 100 : 0;
                  return (
                    <div key={r.restaurant_id} data-testid={`per-restaurant-${r.restaurant_id}`}>
                      <div className="flex items-center justify-between text-sm mb-1.5">
                        <span className="font-medium">{r.name}</span>
                        <span className="font-mono text-zinc-500">{r.count}{showPrices ? ` × ${formatEUR(r.total)}` : ""}</span>
                      </div>
                      <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                        <div className="h-full bg-[#5BC044] rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <InvoiceWidget hidden={!modules.invoices} />

      {isAdmin && modules.orders && stats.low_stock && stats.low_stock.length > 0 && (
        <div className="card-flat p-6 mt-4 border-l-4 border-l-amber-500" data-testid="low-stock-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-amber-50 text-amber-600 flex items-center justify-center">
                <AlertTriangle size={20} />
              </div>
              <div>
                <div className="overline">Voorraadwaarschuwing</div>
                <h3 className="text-xl font-bold tracking-tight text-[#1B2D5C]">Producten onder minimum</h3>
              </div>
            </div>
            <Link to="/voorraad" className="text-sm text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1">
              Naar voorraad <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {stats.low_stock.map((it) => (
              <div key={it.id} className="flex items-center justify-between gap-2 py-2 px-3 border border-amber-200 bg-amber-50/40 rounded-md text-sm" data-testid={`low-stock-${it.id}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{it.product_name}</div>
                  <div className="text-xs text-zinc-500 truncate">{it.restaurant_name}</div>
                </div>
                <div className="font-mono text-xs text-amber-700 shrink-0">
                  {it.stock} / {it.min_stock} {it.unit}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="card-flat p-5" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between mb-3">
        <div className={`w-9 h-9 rounded-md flex items-center justify-center ${accent ? "bg-[#5BC044]/10 text-[#5BC044]" : "bg-zinc-100 text-zinc-700"}`}>
          <Icon size={18} strokeWidth={1.8} />
        </div>
      </div>
      <div className="overline">{label}</div>
      <div className="text-3xl font-bold tracking-tight mt-1 font-mono">{value}</div>
    </div>
  );
}

function MetaCard({ icon: Icon, label, value }) {
  return (
    <div className="card-flat p-5 flex items-center justify-between" data-testid={`meta-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div>
        <div className="overline">{label}</div>
        <div className="text-2xl font-bold tracking-tight mt-1 font-mono">{value}</div>
      </div>
      <Icon size={26} strokeWidth={1.4} className="text-zinc-300" />
    </div>
  );
}

function StatusRow() { return null; } // unused after status removal

function InvoiceWidget({ hidden }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [checks, setChecks] = useState([]);

  useEffect(() => {
    if (hidden) return;
    Promise.all([
      api.get("/invoice_checks/stats"),
      api.get("/invoice_checks?limit=5"),
    ]).then(([s, c]) => {
      setStats(s.data);
      setChecks(c.data || []);
    }).catch(() => { setStats({ totaal: 0, goedgekeurd: 0, openstaand: 0, hoog_risico: 0 }); setChecks([]); });
  }, [hidden]);

  if (hidden) return null;

  const RISK_BADGE = {
    laag:   "bg-emerald-50 text-emerald-700",
    midden: "bg-amber-50 text-amber-700",
    hoog:   "bg-red-50 text-red-700",
  };
  const STATUS_BADGE = {
    open:      "bg-zinc-100 text-zinc-700",
    approved:  "bg-emerald-50 text-emerald-700",
    contacted: "bg-blue-50 text-blue-700",
  };
  const STATUS_LABEL = { open: "Open", approved: "Goedgekeurd", contacted: "Gecontacteerd" };

  return (
    <div className="mt-6" data-testid="invoice-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="overline">Factuurcontroles</div>
        <button
          onClick={() => navigate("/facturen?tab=controle")}
          className="inline-flex items-center gap-1.5 h-9 px-4 bg-[#5BC044] hover:bg-[#4FAA3A] text-white rounded-full text-sm font-semibold"
          data-testid="dashboard-invoice-check-cta"
        >
          <Zap size={14} /> AI Factuurcontrole
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {[
          { label: "Totaal gecontroleerd", val: stats?.totaal ?? "—", color: "text-[#1B2D5C]" },
          { label: "Goedgekeurd", val: stats?.goedgekeurd ?? "—", color: "text-emerald-600" },
          { label: "Openstaand", val: stats?.openstaand ?? "—", color: "text-amber-500" },
          { label: "Hoog risico", val: stats?.hoog_risico ?? "—", color: "text-red-500" },
        ].map((k) => (
          <div key={k.label} className="card-flat p-4 text-center" data-testid={`invoice-kpi-${k.label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className={`text-3xl font-bold font-mono ${k.color}`}>{k.val}</div>
            <div className="text-xs text-zinc-500 font-semibold mt-0.5">{k.label}</div>
          </div>
        ))}
      </div>

      <div className="card-flat p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold text-[#1B2D5C]">Recente factuurcontroles</h3>
        </div>
        {checks.length === 0 ? (
          <div className="text-sm text-zinc-500 py-6 text-center border border-dashed border-zinc-200 rounded-md">
            Nog geen factuurcontroles. Klik op "AI Factuurcontrole" om te beginnen.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left">
                  {["Bestelling", "Leverancier", "Risico", "Status", "Datum"].map((h) => (
                    <th key={h} className="overline pb-2 px-2 border-b border-zinc-100">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {checks.map((c) => (
                  <tr key={c.id} className="border-b border-zinc-50 hover:bg-zinc-50/60" data-testid={`invoice-row-${c.id}`}>
                    <td className="px-2 py-2 font-semibold">{c.order_readable_id || (c.order_id ? c.order_id.slice(-8) : "—")}</td>
                    <td className="px-2 py-2 text-zinc-600">{c.supplier || c.restaurant_name || "—"}</td>
                    <td className="px-2 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${RISK_BADGE[c.risk] || "bg-zinc-100 text-zinc-600"}`}>
                        {c.risk ? c.risk.charAt(0).toUpperCase() + c.risk.slice(1) : "—"}
                      </span>
                    </td>
                    <td className="px-2 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${STATUS_BADGE[c.status] || "bg-zinc-100 text-zinc-600"}`}>
                        {STATUS_LABEL[c.status] || c.status}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-zinc-500 text-xs">{c.created_at ? formatDate(c.created_at) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}


// ─── Horeca-Inspectie compliance-balk (admin) ─────────────────────────
function ComplianceCard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/dashboard/compliance").then((r) => setData(r.data)).catch(() => {});
  }, []);
  const score = data?.score ?? 0;
  const color = score >= 90 ? "text-emerald-600" : score >= 70 ? "text-amber-600" : "text-red-600";
  const bar = score >= 90 ? "bg-emerald-500" : score >= 70 ? "bg-amber-500" : "bg-red-500";
  const label = score >= 90 ? "Waterdicht" : score >= 70 ? "Aandachtspunten" : "Actie vereist";
  return (
    <div className="card-flat p-6 lg:col-span-2" data-testid="compliance-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-md bg-[#1B2D5C]/10 text-[#1B2D5C] flex items-center justify-center">
          <ShieldCheck size={20} />
        </div>
        <div>
          <div className="overline">Horeca-Inspectie</div>
          <h3 className="text-xl font-bold tracking-tight">Compliance-status</h3>
        </div>
      </div>
      {!data ? (
        <div className="text-sm text-zinc-500">Laden…</div>
      ) : (
        <>
          <div className="flex items-baseline justify-between mb-2">
            <div className={`text-4xl font-bold font-mono ${color}`} data-testid="compliance-score">{score}%</div>
            <div className={`text-sm font-semibold ${color}`}>{label}</div>
          </div>
          <div className="h-3 bg-zinc-100 rounded-full overflow-hidden mb-5">
            <div className={`h-full ${bar} transition-all`} style={{ width: `${score}%` }} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <ComplianceRow
              label="Hygiëne deze week"
              done={data.breakdown.hygiene.done}
              total={data.breakdown.hygiene.total}
              score={data.breakdown.hygiene.score}
              testid="compliance-hygiene"
            />
            <ComplianceRow
              label="Vergunningen geldig"
              done={data.breakdown.documents.ok}
              total={data.breakdown.documents.total}
              score={data.breakdown.documents.score}
              testid="compliance-documents"
            />
            <ComplianceRow
              label="Keuringen op tijd"
              done={data.breakdown.inspections.ok}
              total={data.breakdown.inspections.total}
              score={data.breakdown.inspections.score}
              testid="compliance-inspections"
            />
          </div>
        </>
      )}
    </div>
  );
}

function ComplianceRow({ label, done, total, score, testid }) {
  const barColor = score >= 90 ? "bg-emerald-500" : score >= 70 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="p-3 border border-zinc-100 rounded-md" data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs text-zinc-500">{label}</div>
        <div className="text-xs font-mono text-zinc-600">{done}/{total}</div>
      </div>
      <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
        <div className={`h-full ${barColor}`} style={{ width: `${score}%` }} />
      </div>
    </div>
  );
}

// ─── Huidige Bezetting (admin) ───────────────────────────────────────
function OccupancyCard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    const load = () => api.get("/dashboard/current_occupancy").then((r) => setData(r.data)).catch(() => {});
    load();
    const int = setInterval(load, 60000); // auto-refresh every minute
    return () => clearInterval(int);
  }, []);
  return (
    <div className="card-flat p-6" data-testid="occupancy-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-emerald-50 text-emerald-700 flex items-center justify-center">
            <Users size={20} />
          </div>
          <div>
            <div className="overline">Wie is er binnen?</div>
            <h3 className="text-xl font-bold tracking-tight">Huidige bezetting</h3>
          </div>
        </div>
        <div className="text-3xl font-bold font-mono text-[#1B2D5C]" data-testid="occupancy-count">{data?.count ?? 0}</div>
      </div>
      {!data ? (
        <div className="text-sm text-zinc-500">Laden…</div>
      ) : data.count === 0 ? (
        <div className="text-sm text-zinc-500 py-4">Geen personeel ingeklokt volgens de planning voor dit moment.</div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2 mb-4">
            {data.by_function.map((f) => (
              <div key={f.function || "leeg"} className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100" data-testid={`occ-func-${f.function || "leeg"}`}>
                {f.label}: <span className="font-mono">{f.count}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-zinc-100 pt-3 space-y-1.5 max-h-64 overflow-y-auto">
            {data.employees.map((e, i) => (
              <div key={`${e.employee_id}-${i}`} className="flex items-center justify-between text-sm py-1" data-testid={`occ-employee-${i}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{e.name || "—"}</div>
                  <div className="text-xs text-zinc-500 truncate">{e.function_label} · {e.restaurant_name}</div>
                </div>
                <div className="text-xs font-mono text-zinc-600 shrink-0 ml-2">{e.start_time}–{e.end_time}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Openstaande dagplichten (checklist) ─────────────────────────────
function HygieneTasksCard({ isAdmin }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/dashboard/hygiene_status").then((r) => setData(r.data)).catch(() => {});
  }, []);
  const items = data?.items || [];
  // Extra veiligheid: dedupe op (restaurant_id + type) — voor het geval een user
  // door legacy data of samenvoegingen dezelfde restaurant twee keer terug krijgt.
  const uniqueItems = (() => {
    const seen = new Set();
    const out = [];
    for (const it of items) {
      const k = `${it.restaurant_id}::${it.type}`;
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(it);
    }
    return out;
  })();
  // Group by restaurant when admin has multiple; chef sees a flat list.
  const groups = {};
  for (const it of uniqueItems) {
    if (!groups[it.restaurant_id]) groups[it.restaurant_id] = { name: it.restaurant_name, items: [] };
    groups[it.restaurant_id].items.push(it);
  }
  const groupList = Object.values(groups);
  const showRestoName = isAdmin && groupList.length > 1;
  const total = uniqueItems.length;
  const done = uniqueItems.filter((i) => i.filled).length;
  const percent = total ? Math.round((done / total) * 100) : 0;
  return (
    <div className="card-flat p-6" data-testid="hygiene-tasks-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-blue-50 text-blue-700 flex items-center justify-center">
            <ClipboardList size={20} />
          </div>
          <div>
            <div className="overline">Deze week (week {data?.week_nr ?? "—"}) · Alleen hygiëne</div>
            <h3 className="text-xl font-bold tracking-tight">{isAdmin ? "Openstaande dagplichten" : "Compliance Status"}</h3>
          </div>
        </div>
        <Link to="/documenten" className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1" data-testid="hygiene-tasks-open">
          Openen <ArrowUpRight size={13} />
        </Link>
      </div>
      {!data ? (
        <div className="text-sm text-zinc-500">Laden…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-zinc-500 py-4">Geen dagplichten gevonden — controleer of Hygiëne is ingeschakeld voor je restaurant.</div>
      ) : (
        <>
          <div className="flex items-baseline justify-between text-xs text-zinc-500 mb-2">
            <span data-testid="hygiene-tasks-progress">{done}/{total} ingevuld</span>
            <span className="font-mono">{percent}%</span>
          </div>
          <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden mb-4">
            <div className={`h-full ${percent >= 80 ? "bg-emerald-500" : percent >= 50 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${percent}%` }} />
          </div>
          <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
            {groupList.map((g) => (
              <div key={g.name}>
                {showRestoName && (
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400 mb-1">{g.name}</div>
                )}
                <ul className="space-y-1">
                  {g.items.map((it) => {
                    const badge = it.status === "done"
                      ? { label: "Voltooid", cls: "bg-emerald-50 text-emerald-700 border-emerald-100" }
                      : it.status === "overdue"
                        ? { label: "Verlopen", cls: "bg-red-50 text-red-700 border-red-100" }
                        : { label: "Te doen", cls: "bg-amber-50 text-amber-700 border-amber-100" };
                    return (
                      <Link
                        key={`${it.restaurant_id}-${it.type}`}
                        to={`/documenten?open=${it.type}&restaurant_id=${it.restaurant_id}`}
                        className={`flex items-center gap-2 text-sm py-1.5 px-2 rounded-md transition-colors ${it.filled ? "text-emerald-800 bg-emerald-50/60 hover:bg-emerald-100/60" : "text-zinc-700 hover:bg-zinc-50"}`}
                        data-testid={`hygiene-task-${it.restaurant_id}-${it.type}`}>
                        {it.filled ? (
                          <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
                        ) : it.status === "overdue" ? (
                          <XCircle size={16} className="text-red-600 shrink-0" />
                        ) : (
                          <Circle size={16} className="text-zinc-300 shrink-0" />
                        )}
                        <span className={`flex-1 font-semibold ${it.filled ? "line-through opacity-70" : ""}`}>
                          {it.label}
                          {it.week_nr ? <span className="ml-1.5 text-[10px] font-mono text-zinc-400 font-normal">(wk {it.week_nr})</span> : null}
                        </span>
                        {it.due_date && (
                          <span className="text-[10px] font-mono text-zinc-400 shrink-0">t/m {it.due_date.slice(5)}</span>
                        )}
                        <span className={`text-[10px] font-semibold px-1.5 h-5 inline-flex items-center rounded border ${badge.cls} shrink-0`} data-testid={`hygiene-badge-${it.restaurant_id}-${it.type}`}>
                          {badge.label}
                        </span>
                      </Link>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Keuringskalender ─────────────────────────────────────────────────
function InspectionsCard({ isAdmin }) {
  const [data, setData] = useState(null);
  const [manageOpen, setManageOpen] = useState(false);
  const load = () => api.get("/dashboard/inspections").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const items = data?.items || [];
  const upcoming = items.slice(0, 8);
  const overdueCount = items.filter((i) => i.status === "over").length;
  const warnCount = items.filter((i) => i.status === "warn").length;
  return (
    <div className="card-flat p-6" data-testid="inspections-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-amber-50 text-amber-700 flex items-center justify-center">
            <Calendar size={20} />
          </div>
          <div>
            <div className="overline">Verplichte periodieke keuringen</div>
            <h3 className="text-xl font-bold tracking-tight">Keuringskalender</h3>
          </div>
        </div>
        {isAdmin && (
          <button
            onClick={() => setManageOpen(true)}
            className="text-xs text-zinc-500 hover:text-[#1B2D5C] inline-flex items-center gap-1"
            data-testid="inspections-manage-open"
          >
            <Pencil size={12} /> Beheer
          </button>
        )}
      </div>
      {!data ? (
        <div className="text-sm text-zinc-500">Laden…</div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2 mb-3 text-xs">
            {overdueCount > 0 && (
              <span className="inline-flex items-center gap-1 px-2 h-6 rounded-full bg-red-50 text-red-700 border border-red-100 font-semibold" data-testid="inspections-overdue">
                <XCircle size={12} /> {overdueCount} over datum
              </span>
            )}
            {warnCount > 0 && (
              <span className="inline-flex items-center gap-1 px-2 h-6 rounded-full bg-amber-50 text-amber-700 border border-amber-100 font-semibold" data-testid="inspections-warn">
                <AlertTriangle size={12} /> {warnCount} binnen 30 dgn
              </span>
            )}
            {overdueCount === 0 && warnCount === 0 && (
              <span className="inline-flex items-center gap-1 px-2 h-6 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 font-semibold" data-testid="inspections-all-ok">
                <CheckCircle2 size={12} /> Alles op planning
              </span>
            )}
          </div>
          <ul className="space-y-1.5 max-h-72 overflow-y-auto">
            {upcoming.length === 0 ? (
              <li className="text-sm text-zinc-500 py-3">Nog geen keuringen gepland.</li>
            ) : upcoming.map((it) => (
              <li key={it.id}
                  className={`flex items-center justify-between text-sm py-2 px-2 rounded-md ${
                    it.status === "over" ? "bg-red-50/60" : it.status === "warn" ? "bg-amber-50/60" : "hover:bg-zinc-50"
                  }`}
                  data-testid={`inspection-${it.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="truncate">{it.name}</div>
                  <div className="text-xs text-zinc-500 truncate">{it.restaurant_name}</div>
                </div>
                <div className={`text-xs font-mono ml-2 shrink-0 text-right ${
                  it.status === "over" ? "text-red-600 font-semibold" : it.status === "warn" ? "text-amber-700 font-semibold" : "text-zinc-500"
                }`}>
                  {it.next_due}
                  <div className="text-[10px]">
                    {it.status === "over" ? `${Math.abs(it.days_left)}d over` : `over ${it.days_left}d`}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
      {manageOpen && (
        <InspectionsManageModal onClose={() => { setManageOpen(false); load(); }} />
      )}
    </div>
  );
}

function InspectionsManageModal({ onClose }) {
  const [restos, setRestos] = useState([]);
  const [restoId, setRestoId] = useState("");
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newItem, setNewItem] = useState({ name: "", interval_months: 12, next_due: "" });

  useEffect(() => {
    api.get("/restaurants").then((r) => {
      setRestos(r.data || []);
      if (r.data?.length) setRestoId(r.data[0].id);
    });
  }, []);
  useEffect(() => {
    if (!restoId) return;
    api.get(`/inspections?restaurant_id=${restoId}`).then((r) => setItems(r.data || []));
  }, [restoId]);

  const markDone = async (id) => {
    setSaving(true);
    try {
      const r = await api.put(`/inspections/${id}`, { mark_done: true });
      setItems(items.map((it) => it.id === id ? r.data : it));
    } finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!confirm("Deze keuring verwijderen?")) return;
    await api.delete(`/inspections/${id}`);
    setItems(items.filter((it) => it.id !== id));
  };
  const updateField = async (id, field, value) => {
    const r = await api.put(`/inspections/${id}`, { [field]: value });
    setItems(items.map((it) => it.id === id ? r.data : it));
  };
  const addItem = async () => {
    if (!newItem.name.trim()) return;
    const r = await api.post("/inspections", { restaurant_id: restoId, ...newItem });
    setItems([...items, r.data]);
    setNewItem({ name: "", interval_months: 12, next_due: "" });
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="inspections-manage-modal">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-100">
          <div>
            <div className="font-bold text-[#1B2D5C] text-base">Keuringskalender beheren</div>
            <div className="text-xs text-zinc-500">Stel intervallen, laatste keuring en volgende vervaldatum in per restaurant.</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-md hover:bg-zinc-100 inline-flex items-center justify-center" data-testid="inspections-manage-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4 overflow-auto">
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-zinc-500">Restaurant:</label>
            <select value={restoId} onChange={(e) => setRestoId(e.target.value)}
                    className="h-9 border border-zinc-200 rounded-md px-2 text-sm" data-testid="inspections-manage-resto">
              {restos.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-zinc-500 border-b border-zinc-100">
                <th className="py-2">Naam</th>
                <th className="py-2 w-24">Interval (mnd)</th>
                <th className="py-2 w-32">Laatste</th>
                <th className="py-2 w-32">Volgende</th>
                <th className="py-2 w-32 text-right">Acties</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b border-zinc-50" data-testid={`inspection-row-${it.id}`}>
                  <td className="py-1.5">
                    <input defaultValue={it.name}
                           onBlur={(e) => e.target.value !== it.name && updateField(it.id, "name", e.target.value)}
                           className="w-full h-8 border border-transparent hover:border-zinc-200 rounded px-1.5 text-sm" />
                  </td>
                  <td className="py-1.5">
                    <input type="number" defaultValue={it.interval_months} min="1"
                           onBlur={(e) => Number(e.target.value) !== it.interval_months && updateField(it.id, "interval_months", Number(e.target.value))}
                           className="w-full h-8 border border-transparent hover:border-zinc-200 rounded px-1.5 text-sm font-mono" />
                  </td>
                  <td className="py-1.5">
                    <input type="date" defaultValue={it.last_done || ""}
                           onBlur={(e) => e.target.value !== (it.last_done || "") && updateField(it.id, "last_done", e.target.value || null)}
                           className="w-full h-8 border border-transparent hover:border-zinc-200 rounded px-1.5 text-xs font-mono" />
                  </td>
                  <td className="py-1.5">
                    <input type="date" defaultValue={it.next_due || ""}
                           onBlur={(e) => e.target.value !== (it.next_due || "") && updateField(it.id, "next_due", e.target.value)}
                           className="w-full h-8 border border-transparent hover:border-zinc-200 rounded px-1.5 text-xs font-mono" />
                  </td>
                  <td className="py-1.5 text-right">
                    <button onClick={() => markDone(it.id)} disabled={saving}
                            className="inline-flex items-center gap-1 h-8 px-2 border border-emerald-200 hover:border-emerald-400 text-emerald-700 rounded text-xs font-semibold mr-1"
                            data-testid={`inspection-mark-done-${it.id}`}>
                      <CheckCircle2 size={12} /> Gedaan
                    </button>
                    <button onClick={() => remove(it.id)}
                            className="inline-flex items-center h-8 w-8 justify-center border border-red-200 hover:border-red-400 text-red-600 rounded"
                            data-testid={`inspection-delete-${it.id}`}>
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
              <tr>
                <td className="py-2">
                  <input value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                         placeholder="Nieuwe keuring…"
                         className="w-full h-8 border border-zinc-200 rounded px-1.5 text-sm" data-testid="inspection-new-name" />
                </td>
                <td className="py-2">
                  <input type="number" value={newItem.interval_months} min="1" onChange={(e) => setNewItem({ ...newItem, interval_months: Number(e.target.value) })}
                         className="w-full h-8 border border-zinc-200 rounded px-1.5 text-sm font-mono" />
                </td>
                <td className="py-2 text-xs text-zinc-400 italic">auto</td>
                <td className="py-2">
                  <input type="date" value={newItem.next_due} onChange={(e) => setNewItem({ ...newItem, next_due: e.target.value })}
                         className="w-full h-8 border border-zinc-200 rounded px-1.5 text-xs font-mono" />
                </td>
                <td className="py-2 text-right">
                  <button onClick={addItem} disabled={!newItem.name.trim()}
                          className="inline-flex items-center gap-1 h-8 px-3 bg-[#5BC044] hover:bg-[#4FAA3A] text-white rounded text-xs font-semibold disabled:opacity-50"
                          data-testid="inspection-add">
                    <Plus size={12} /> Toevoegen
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── HACCP Training voortgang (admin) ────────────────────────────────
function TrainingProgressCard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/training/haccp/team").then((r) => setData(r.data)).catch(() => {});
  }, []);
  const items = data?.items || [];
  const certified = data?.certified || 0;
  const total = data?.total || 0;
  return (
    <div className="card-flat p-6 lg:col-span-2" data-testid="training-progress-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-emerald-50 text-emerald-700 flex items-center justify-center">
            <GraduationCap size={20} />
          </div>
          <div>
            <div className="overline">HACCP Hygiënecode Horeca</div>
            <h3 className="text-xl font-bold tracking-tight">Training voortgang</h3>
          </div>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold font-mono text-emerald-700" data-testid="training-certified-count">{certified}/{total}</div>
          <div className="text-[11px] text-zinc-500 uppercase tracking-wide">gecertificeerd</div>
        </div>
      </div>
      {!data ? (
        <div className="text-sm text-zinc-500">Laden…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-zinc-500 py-4">Nog geen team-leden gevonden.</div>
      ) : (
        <div className="overflow-hidden">
          <div className="grid grid-cols-[1fr_auto_auto] items-center gap-x-4 gap-y-1 text-xs text-zinc-400 uppercase tracking-wide pb-2 border-b border-zinc-100">
            <div>Medewerker</div>
            <div className="text-center">Niveaus (1–5)</div>
            <div className="text-right">Status</div>
          </div>
          <div className="max-h-96 overflow-y-auto divide-y divide-zinc-50">
            {items.map((it) => (
              <div key={it.user_id} className="grid grid-cols-[1fr_auto_auto] items-center gap-x-4 py-2" data-testid={`training-row-${it.user_id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{it.name || it.email}</div>
                  <div className="text-[11px] text-zinc-500 truncate">
                    {it.role === "chef" ? "Chef" : "Admin"}{it.restaurant_name ? ` · ${it.restaurant_name}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-1" data-testid={`training-levels-${it.user_id}`}>
                  {[1, 2, 3, 4, 5].map((L) => {
                    const passed = it.levels_passed.includes(L);
                    const score = it.best_scores?.[L] ?? it.best_scores?.[String(L)];
                    return (
                      <span
                        key={L}
                        title={score != null ? `Niveau ${L}: ${score}%` : `Niveau ${L}: nog niet behaald`}
                        className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold border ${
                          passed
                            ? "bg-emerald-500 text-white border-emerald-600"
                            : "bg-zinc-50 text-zinc-400 border-zinc-200"
                        }`}
                      >
                        {L}
                      </span>
                    );
                  })}
                </div>
                <div className="text-right">
                  {it.certified ? (
                    <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 text-[11px] font-semibold" data-testid={`training-cert-badge-${it.user_id}`}>
                      <Award size={11} /> Gecertificeerd
                    </span>
                  ) : it.levels_passed.length > 0 ? (
                    <span className="inline-flex items-center h-6 px-2 rounded-full bg-amber-50 text-amber-700 border border-amber-100 text-[11px] font-semibold">
                      {it.levels_passed.length}/5 behaald
                    </span>
                  ) : (
                    <span className="inline-flex items-center h-6 px-2 rounded-full bg-zinc-50 text-zinc-500 border border-zinc-200 text-[11px]">
                      Nog niet gestart
                    </span>
                  )}
                  {it.total_retakes > 0 && (
                    <div className="text-[10px] text-orange-600 mt-1" data-testid={`training-retakes-${it.user_id}`}
                         title="Aantal keer een niveau opnieuw gedaan — kan wijzen op behoefte aan extra begeleiding">
                      {it.total_retakes}× retake
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

